<template>
  <div class="flex flex-col w-full h-full">
    <!-- Page header -->
    <div class="flex items-center gap-3 px-4 pt-4">
      <span
        class="flex items-center justify-center w-10 h-10 rounded-xl bg-taxiye-100 text-taxiye-500"
      >
        <span class="material-icons-round">insights</span>
      </span>
      <div>
        <h2 class="text-lg font-semibold text-gray-800">Dashboard</h2>
        <p class="text-sm text-gray-500">
          Overview of your corporate site statistics.
        </p>
      </div>
    </div>

    <div class="m-4 mt-3 space-y-6">
      <!-- Stat cards -->
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div class="p-5 bg-white border border-gray-200 shadow-sm rounded-2xl">
          <span
            class="flex items-center justify-center w-11 h-11 rounded-xl bg-indigo-100 text-indigo-600"
          >
            <span class="material-icons-round">person</span>
          </span>
          <p class="mt-4 text-sm text-gray-500">Total Trips</p>
          <p class="text-3xl font-semibold text-gray-800">
            {{ stats.totalTrips }}
          </p>
        </div>

        <div class="p-5 bg-white border border-gray-200 shadow-sm rounded-2xl">
          <span
            class="flex items-center justify-center w-11 h-11 rounded-xl bg-blue-100 text-blue-600"
          >
            <span class="material-icons-round">theaters</span>
          </span>
          <p class="mt-4 text-sm text-gray-500">Total Tickets</p>
          <p class="text-3xl font-semibold text-gray-800">
            {{ stats.tickets }}
          </p>
        </div>

        <div class="p-5 bg-white border border-gray-200 shadow-sm rounded-2xl">
          <span
            class="flex items-center justify-center w-11 h-11 rounded-xl bg-green-100 text-green-600"
          >
            <span class="material-icons-round">attach_money</span>
          </span>
          <p class="mt-4 text-sm text-gray-500">Current Month Cost</p>
          <p class="text-3xl font-semibold text-gray-800">
            {{ stats.monthlyCost.toFixed(2) }}
          </p>
        </div>

        <div class="p-5 bg-white border border-gray-200 shadow-sm rounded-2xl">
          <span
            class="flex items-center justify-center w-11 h-11 rounded-xl bg-teal-100 text-teal-600"
          >
            <span class="material-icons-round">local_taxi</span>
          </span>
          <p class="mt-4 text-sm text-gray-500">Current Month Trips</p>
          <p class="text-3xl font-semibold text-gray-800">
            {{ stats.monthlyTrip }}
          </p>
        </div>
      </div>

      <!-- Trips -->
      <div class="space-y-4">
        <h3 class="text-base font-semibold text-gray-800">Trips</h3>

        <div class="flex flex-wrap items-center justify-between gap-4">
          <v-date-picker v-model="range" is-range timezone="UTC">
            <template v-slot="{ inputValue, inputEvents }">
              <div class="flex items-center">
                <input
                  :value="inputValue.start"
                  v-on="inputEvents.start"
                  placeholder="From Date"
                  class="w-32 h-10 text-sm border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                />
                <span class="mx-2 text-gray-400 material-icons-round"
                  >arrow_forward</span
                >
                <input
                  :value="inputValue.end"
                  v-on="inputEvents.end"
                  placeholder="To Date"
                  class="w-32 h-10 text-sm border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                />
              </div>
            </template>
          </v-date-picker>

          <div class="flex items-center gap-4">
            <div
              class="px-5 py-3 text-right bg-white border border-gray-200 shadow-sm rounded-2xl"
            >
              <p class="text-sm text-gray-500">Number of Trips</p>
              <p class="text-2xl font-semibold text-gray-800">
                {{ rides.length }}
              </p>
            </div>
            <div
              class="px-5 py-3 text-right bg-white border border-gray-200 shadow-sm rounded-2xl"
            >
              <p class="text-sm text-gray-500">Cost</p>
              <p class="text-2xl font-semibold text-gray-800">{{ fareSum }}</p>
            </div>
          </div>
        </div>

        <div
          class="overflow-hidden bg-white border border-gray-200 shadow-sm rounded-2xl"
        >
          <div class="overflow-x-auto">
            <table class="min-w-full">
              <thead>
                <tr>
                  <th
                    class="px-5 py-3 text-xs font-semibold tracking-wider text-left text-gray-500 uppercase bg-gray-50 border-b border-gray-200"
                  >
                    Trip ID
                  </th>
                  <th
                    class="px-5 py-3 text-xs font-semibold tracking-wider text-left text-gray-500 uppercase bg-gray-50 border-b border-gray-200"
                  >
                    Passenger Name
                  </th>
                  <th
                    class="px-5 py-3 text-xs font-semibold tracking-wider text-left text-gray-500 uppercase bg-gray-50 border-b border-gray-200"
                  >
                    Driver Name
                  </th>
                  <th
                    class="px-5 py-3 text-xs font-semibold tracking-wider text-left text-gray-500 uppercase bg-gray-50 border-b border-gray-200"
                  >
                    Pick / Drop Address
                  </th>
                  <th
                    class="px-5 py-3 text-xs font-semibold tracking-wider text-left text-gray-500 uppercase bg-gray-50 border-b border-gray-200"
                  >
                    Date
                  </th>
                  <th
                    class="px-5 py-3 text-xs font-semibold tracking-wider text-left text-gray-500 uppercase bg-gray-50 border-b border-gray-200"
                  >
                    Trip Fare
                  </th>
                  <th
                    class="px-5 py-3 text-xs font-semibold tracking-wider text-left text-gray-500 uppercase bg-gray-50 border-b border-gray-200"
                  >
                    Status
                  </th>
                </tr>
              </thead>
              <tbody v-if="rides.length">
                <tr
                  v-for="trip in rides"
                  :key="trip._id"
                  class="transition-colors hover:bg-gray-50"
                >
                  <td
                    class="px-5 py-4 text-sm text-gray-700 border-b border-gray-100"
                  >
                    {{ trip._id }}
                  </td>
                  <td
                    class="px-5 py-4 text-sm text-gray-700 border-b border-gray-100"
                  >
                    {{
                      `${trip.passenger.firstName} ${
                        trip.passenger.lastName ? trip.passenger.lastName : ""
                      }`
                    }}
                  </td>
                  <td
                    class="px-5 py-4 text-sm text-gray-700 border-b border-gray-100"
                  >
                    {{ trip.driver.firstName + " " + trip.driver.lastName }}
                  </td>
                  <td
                    class="px-5 py-4 text-sm text-gray-700 border-b border-gray-100"
                  >
                    <div class="flex items-center space-x-2">
                      <span
                        class="text-base text-green-600 material-icons-round"
                        >location_on</span
                      >
                      <span>{{ trip.pickUpAddress.name }}</span>
                    </div>
                    <div class="flex items-center mt-1 space-x-2">
                      <span class="text-base text-red-600 material-icons-round"
                        >location_on</span
                      >
                      <span>{{ trip.dropOffAddress.name }}</span>
                    </div>
                  </td>
                  <td
                    class="px-5 py-4 text-sm text-gray-700 border-b border-gray-100"
                  >
                    <p class="whitespace-no-wrap">
                      {{ formatDate(trip.pickupTimestamp) }}
                    </p>
                    <p class="mt-1 whitespace-no-wrap">
                      {{ formatDate(trip.endTimestamp) }}
                    </p>
                  </td>
                  <td
                    class="px-5 py-4 text-sm text-gray-700 border-b border-gray-100"
                  >
                    {{ trip.fare ? trip.fare.toFixed(2) : "0" }}
                  </td>
                  <td class="px-5 py-4 text-sm border-b border-gray-100">
                    <span
                      class="inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700"
                    >
                      {{ trip.status }}
                    </span>
                  </td>
                </tr>
              </tbody>
              <tbody v-else>
                <tr>
                  <td colspan="7" class="px-5 py-10 text-center">
                    <div
                      v-if="loading"
                      class="inline-flex flex-col items-center text-gray-400"
                    >
                      <span class="text-5xl material-icons-round spin"
                        >sync</span
                      >
                      <span class="mt-2 text-sm">Loading...</span>
                    </div>
                    <div
                      v-else
                      class="inline-flex flex-col items-center text-gray-400"
                    >
                      <span class="text-5xl material-icons-round">warning</span>
                      <span class="mt-2 text-sm">No latest trips!</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// import moment from "moment";

export default {
  name: "Dashboard",
  data() {
    return {
      range: {
        start: null,
        end: null,
      },
      stats: {
        totalTrips: 0,
        monthlyTrip: 0,
        tickets: 0,
        monthlyCost: 0,
      },
      rides: [],
      loading: false,
    };
  },
  created() {
    this.getStats();
    this.getRides();

    console.log(this.$store.getters.isLoggedIn);
  },
  computed: {
    corporateId() {
      return this.$store.state.auth.account.corporate
        ? this.$store.state.auth.account.corporate._id
        : null;
    },
    fareSum() {
      return this.rides.length
        ? this.rides
            .map(trip => trip.fare)
            .reduce((fare, current) => fare + current)
            .toFixed(2)
        : 0;
    },
  },
  watch: {
    range: {
      deep: true,
      handler() {
        this.getRides();
      },
    },
  },
  methods: {
    getStats() {
      this.$http
        .get(`/corporates/${this.corporateId}/dashboard`)
        .then(response => (this.stats = response.data))
        .catch(err => console.log(err));
    },
    getRides() {
      const { range } = this;
      let url = `/corporates/${this.corporateId}/trips?populate=["driver", "passenger"]`;

      if (range.start) url += `start=${new Date(range.start).toISOString()}`;
      if (range.end) url += `&end=${new Date(range.end).toISOString()}`;

      this.loading = true;
      this.$misc
        .get(url)
        .then(response => {
          this.rides = response.data.data;
        })
        .catch(err => console.log(err))
        .finally(() => (this.loading = false));
    },
    formatDate(date) {
      // return moment(date).format("MMM D, YYYY HH:mm");
      return date ? date.replace("T", " ").slice(0, 16) : "-";
    },
  },
};
</script>
